import { fabric } from "fabric";
import { getDeviceDependentDpr, getScreenWidth } from "~/utils";

// fix IText not editable inside modal https://github.com/fabricjs/fabric.js/issues/5126
fabric.IText.prototype.initHiddenTextarea = (function (initHiddenTextArea) {
  return function () {
    const result = initHiddenTextArea.apply(this);
    fabric.document.body.removeChild(this.hiddenTextarea);
    this.hiddenTextarea.style.top = `${this.top}px`;
    this.hiddenTextarea.style.left = `${this.left}px`;
    this.canvas.wrapperEl.appendChild(this.hiddenTextarea);
    return result;
  };
})(fabric.IText.prototype.initHiddenTextarea);

fabric.IText.prototype.updateTextareaPosition = function () {};

const { Ellipse, Rect, IText, Line, Triangle, util, Polygon, Group } = fabric;
const { createClass } = util;

export const LINE_DEFAULT_DIMENSIONS = 18;
const CustomLine = createClass(Line, {
  type: "line",
  MV_PADDING: 20,
  initialize(points = [], options = {}, color) {
    if (getScreenWidth() < 1280) {
      this.MV_PADDING = 40;
    }

    const a = Math.abs(points[2] - points[0]);
    const b = Math.max(Math.abs(points[3] - points[1]), LINE_DEFAULT_DIMENSIONS);
    const len = Math.sqrt(a ** 2 + b ** 2);
    const x = points[0] + this.MV_PADDING / 2 - LINE_DEFAULT_DIMENSIONS / 2;
    const y = points[1] + this.MV_PADDING - len;
    this.callSuper("initialize", [x, y, x, y + len], {
      ...options,
      fill: "",
      selectable: true,
      strokeWidth: 2,
      hasControls: true,
      hasBorders: true,
      originX: "center",
      originY: "top",
      strokeUniform: true,
      lockScalingFlip: true,
      centeredRotation: false,
      padding: this.MV_PADDING
    });

    this.setControlsVisibility({
      bl: false,
      br: false,
      mb: true,
      ml: false,
      mr: false,
      mt: true,
      tl: false,
      tr: false,
      mtr: true
    });
    this.sinusVersion = "3.6.2";

    this.set("originY", "bottom");

    const rotAngle = (Math.acos(b / len) * 180) / Math.PI;
    const getCalcAngle = () => {
      const alpha = points[0] > points[2] ? rotAngle : 360 - rotAngle;
      return points[1] < points[3] ? alpha : 180 - alpha;
    };

    if (color) {
      this.set("stroke", color);
    }
    if (options.strokeWidth) {
      this.set("strokeWidth", options.strokeWidth);
    }
    this.set("angle", options.angle ?? getCalcAngle());
    this.setCoords();
  },
  getPoints() {
    const x1 = this.get("x1");
    const y1 = this.get("y1");
    const x2 = this.get("x2");
    const y2 = this.get("y2");

    return {
      x1,
      y1,
      x2,
      y2
    };
  },
  onMoving() {
    const { x1, y1, x2, y2 } = this.getPoints();
    const { left, top } = this.getPosition();

    const dx = left - (x1 + x2) / 2;
    const dy = top - (y1 + y2) / 2;

    this.set({
      x1: this.x1 + dx,
      y1: this.y1 + dy,
      x2: this.x2 + dx,
      y2: this.y2 + dy
    });

    this.set({
      left: (this.x1 + this.x2) / 2,
      top: (this.y1 + this.y2) / 2
    });

    this.setCoords();
  },
  onMouseMove({ x, y }) {
    const { x2, y2 } = this.getPoints();
    const x1 = x;
    const y1 = y;

    const a = Math.abs(x1 - x2);
    const b = Math.max(Math.abs(y1 - y2), LINE_DEFAULT_DIMENSIONS);
    const len = Math.sqrt(a ** 2 + b ** 2);

    const rotAngle = (Math.acos(b / len) * 180) / Math.PI;
    const alpha = x1 > x2 ? rotAngle : -rotAngle;
    const angle = y1 < y2 ? alpha : 180 - alpha;

    this.set({
      y1: y2 - len
    });
    this.set("angle", angle);
    this.setCoords();
  },
  toObject(propertiesToInclude = []) {
    return this.callSuper("toObject", [...propertiesToInclude]);
  }
});

CustomLine.fromObject = function (object, callback) {
  const options = { ...object };
  options.points = [object.x1, object.y1, object.x2, object.y2];
  fabric.Object._fromObject("Line", options, callback, "points");
};

const CustomRect = createClass(Rect, {
  toObject(propertiesToInclude = []) {
    return this.callSuper("toObject", [...propertiesToInclude, "strokeUniform"]);
  }
});

CustomRect.fromObject = function (object, callback) {
  fabric.Object._fromObject("Rect", object, callback);
};

const CustomEllipse = createClass(Ellipse, {
  toObject(propertiesToInclude = []) {
    return this.callSuper("toObject", [...propertiesToInclude, "strokeUniform"]);
  }
});

CustomEllipse.fromObject = function (object, callback) {
  fabric.Object._fromObject("Ellipse", object, callback);
};

export const InteractiveText = createClass(IText, {
  type: "interactiveText",
  MV_DEFAULT_TEXT: "Klicken zum Bearbeiten",
  initialize(text = this.MV_DEFAULT_TEXT, options) {
    this.callSuper("initialize", text, {
      ...options,
      fontFamily: "Arial",
      lockUniScaling: true,
      lockScalingFlip: true,
      padding: 8
    });
    this.onKeyUp = function (e) {
      if (this.isEditing) e.stopPropagation();
    };
    this.on("editing:entered", function () {
      if (this.text === this.MV_DEFAULT_TEXT) {
        this.updateText("");
      }
    });
    this.on("editing:exited", function () {
      if (this.text.length === 0) {
        this.updateText(this.MV_DEFAULT_TEXT);
      }
    });
  },
  updateText(text) {
    this.text = text;
    if (this.hiddenTextarea) this.hiddenTextarea.value = text;
    this.canvas.renderAll();
  }
});

InteractiveText.fromObject = function (object, callback) {
  fabric.Object._fromObject("InteractiveText", object, callback, "text");
};

export const ARROW_DEFAULT_DIMENSIONS = 18;

export const Arrow = createClass(Line, {
  type: "arrow",
  MV_PADDING: 20,
  initialize(points = [], options = {}, color) {
    if (getScreenWidth() < 1280) {
      this.MV_PADDING = 40;
    }
    const a = Math.abs(points[2] - points[0]);
    const b = Math.max(Math.abs(points[3] - points[1]), ARROW_DEFAULT_DIMENSIONS);
    const len = Math.sqrt(a ** 2 + b ** 2);
    const x = points[0] + this.MV_PADDING / 2 - ARROW_DEFAULT_DIMENSIONS / 2;
    const y = points[1] + this.MV_PADDING - len;

    this.callSuper("initialize", [x, y, x, y + len], {
      strokeWidth: 2,
      ...options,
      fill: "",
      selectable: true,
      hasControls: true,
      hasBorders: true,
      originX: "center",
      originY: "top",
      strokeUniform: true,
      lockScalingFlip: true,
      centeredRotation: false,
      padding: this.MV_PADDING
    });

    this.setControlsVisibility({
      bl: false,
      br: false,
      mb: true,
      ml: false,
      mr: false,
      mt: true,
      tl: false,
      tr: false,
      mtr: true
    });
    this.sinusVersion = "3.6.2";
    this.arrow = new Triangle({
      arrowId: options._id,
      arrowHead: true,
      left: x,
      top: y + len,
      originX: "bottom",
      originY: "center",
      selectable: false,
      hasControls: false,
      hasBorders: false,
      lockScalingX: true,
      lockScalingY: true,
      lockRotation: true,
      angle: this.calculateArrowRotation(),
      width: ARROW_DEFAULT_DIMENSIONS,
      height: ARROW_DEFAULT_DIMENSIONS,
      fill: options.stroke ?? "black",
      centeredRotation: false
    });
    if (color) {
      this.set("stroke", color);
      this.arrow.set("fill", color);
    }
    if (options.strokeWidth) {
      this.set("strokeWidth", options.strokeWidth);
      this.arrow.set("width", ARROW_DEFAULT_DIMENSIONS + options.strokeWidth * 2);
      this.arrow.set("height", ARROW_DEFAULT_DIMENSIONS + options.strokeWidth * 2);
    }

    // move rotation control to arrow head
    this.controls.mtr.y = -0.5;
    // this.controls.mtr.offsetY = -40;
    this.set("originY", "bottom");

    const rotAngle = (Math.acos(b / len) * 180) / Math.PI;
    const getCalcAngle = () => {
      const alpha = points[0] > points[2] ? rotAngle : 360 - rotAngle;
      return points[1] < points[3] ? alpha : 180 - alpha;
    };
    this.set("angle", options.angle ?? getCalcAngle());
    this.setCoords();

    this.on("moving", this.onMoving.bind(this));
    this.on("rotating", this.onMoving.bind(this));
    this.on("scaling", this.onMoving.bind(this));
    this.on("moved", this.onMoving.bind(this));

    this.on("selected", this.onSelected.bind(this));
    this.on("deselected", this.onDeselected.bind(this));
    this.on("added", () => {
      this.canvas.add(this.arrow);
      this.onMoving();
    });
    this.on("removed", () => this.canvas.remove(this.arrow));
  },
  onSelected() {
    this.arrow.bringToFront();
  },
  onDeselected() {
    const z = this.canvas.getObjects().indexOf(this);
    this.arrow.moveTo(z);
  },
  getPosition() {
    const left = this.get("left");
    const top = this.get("top");

    return {
      left,
      top
    };
  },
  getPoints() {
    const x1 = this.get("x1");
    const y1 = this.get("y1");
    const x2 = this.get("x2");
    const y2 = this.get("y2");

    return {
      x1,
      y1,
      x2,
      y2
    };
  },
  updateArrow() {
    this.arrow
      .set({
        left: this.left,
        top: this.top
      })
      .setCoords();
    const angle = this.calculateArrowRotation();
    this.arrow.set("angle", angle);
  },
  onMoving() {
    const { x1, y1, x2, y2 } = this.getPoints();
    const { left, top } = this.getPosition();

    const dx = left - (x1 + x2) / 2;
    const dy = top - (y1 + y2) / 2;

    this.set({
      x1: this.x1 + dx,
      y1: this.y1 + dy,
      x2: this.x2 + dx,
      y2: this.y2 + dy
    });

    this.set({
      left: (this.x1 + this.x2) / 2,
      top: (this.y1 + this.y2) / 2
    });

    this.setCoords();
    this.updateArrow();
  },
  calculateArrowRotation() {
    const { x1, y1, x2, y2 } = this.getPoints();

    const dx = (x2 - x1) * this.scaleX;
    const dy = (y2 - y1) * this.scaleY;
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

    return angle + 90 + this.angle;
  },
  onMouseMove({ x, y }) {
    const { x2, y2 } = this.getPoints();
    const x1 = x;
    const y1 = y;

    const a = Math.abs(x1 - x2);
    const b = Math.max(Math.abs(y1 - y2), 0);
    const len = Math.max(Math.sqrt(a ** 2 + b ** 2), ARROW_DEFAULT_DIMENSIONS);

    const rotAngle = (Math.acos(b / len) * 180) / Math.PI;
    const alpha = x1 > x2 ? rotAngle : -rotAngle;
    const angle = y1 < y2 ? alpha : 180 - alpha;

    this.set({
      y1: y2 - len
    });
    this.set("angle", angle);
    this.setCoords();
    this.updateArrow();
  }
  // toObject() {
  //   const obj = this.callSuper("toObject");
  //   console.log({ obj });
  //   obj.version = "3.6.2";
  //   return obj;
  // }
});

Arrow.fromObject = function (object, callback) {
  function _callback(instance) {
    delete instance.points;
    callback && callback(instance);
  }

  const options = { ...object };
  if (object.version[0] < "5") {
    console.log("older", { object });
    // test this. When objects get selected and save again, they have the new version
    options.points = [
      object.x1 + object.left,
      object.y1 + object.height,
      object.x2 + object.left,
      object.y2 + object.height
    ];
  } else {
    options.points = [object.x1, object.y1, object.x2, object.y2];
  }

  fabric.Object._fromObject("Arrow", options, _callback, "points");
};

export const CustomPolygon = createClass(Polygon, {
  type: "customPolygon",
  MV_PADDING: 20,
  maxPoints: null,
  edit: true,
  initialize(points = [], options = {}, maxPoints = 3) {
    if (getScreenWidth() < 1280) {
      this.MV_PADDING = 40;
    }

    this.maxPoints = maxPoints;
    if (points.length < 2) points.push(points[0]);
    this.callSuper("initialize", points, {
      fill: "",
      strokeWidth: 2,
      ...options,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      originX: "center",
      originY: "top",
      strokeUniform: true,
      lockScalingFlip: true,
      centeredRotation: false,
      padding: this.MV_PADDING,
      objectCaching: false
    });
    this.setControlsVisibility({
      mb: true,
      mtr: true
    });
    if (options.strokeWidth) {
      this.set("strokeWidth", options.strokeWidth);
    }
    this.hasBorders = !this.edit;
    this.setControls();
  },
  // renderIcon(ctx, left, top, styleOverride, fabricObject) {
  //   const size = this.cornerSize;
  //   ctx.save();
  //   ctx.translate(left, top);
  //   ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
  //   ctx.drawImage(toggleEditImg, -size / 2, -size / 2, size, size);
  //   ctx.restore();
  // },
  // addToggleEditControl() {
  //   const newControl = new fabric.Control({
  //     x: 0.5,
  //     y: -0.75,
  //     offset: -16,
  //     cursorStyle: "pointer",
  //     mouseUpHandler: () => this.toggleEdit(),
  //     render: this.renderIcon,
  //     cornerSize: 24
  //   });
  //   this.controls.toggleEdit = newControl;
  // },
  getPoints() {
    return this.get("points");
  },
  getPosition() {
    const left = this.get("left");
    const top = this.get("top");

    return {
      left,
      top
    };
  },
  updateBoundingRect() {
    const { width, height, left, top } = this._calcDimensions();
    this.set({
      hasBorder: true,
      top,
      left: left + width / 2,
      width,
      height,
      pathOffset: new fabric.Point(left + width / 2, top + height / 2)
    });
  },
  onMouseMove({ x, y }) {
    const points = this.getPoints();
    points[points.length - 1] = { x, y };
    this.set({ points });
    this.setCoords();
    this.updateBoundingRect();
  },
  addPoint({ x, y }) {
    const { points } = this;
    points.push({ x, y });
    this.set({ points });
    this.setCoords();
    this.setControls();
  },
  hasPoints(num) {
    return this.points.length === num;
  },
  hasMaxPoints() {
    return this.getPoints().length === this.maxPoints;
  },
  finalize() {
    this.setControlsVisibility({
      mb: true,
      mtr: true
    });
    this.updateBoundingRect();
  },
  getObjectSizeWithStroke() {
    const stroke = new fabric.Point(
      this.strokeUniform ? 1 / this.scaleX : 1,
      this.strokeUniform ? 1 / this.scaleY : 1
    ).multiply(this.strokeWidth);
    return new fabric.Point(this.width + stroke.x, this.height + stroke.y);
  },

  /*
    define a function that will define what the control does
    this function will be called on every mouse move after a control has been
    clicked and is being dragged.
    The function receive as argument the mouse event, the current trasnform object
    and the current position in canvas coordinate
    transform.target is a reference to the current object being transformed,
  */
  getControlActionHandler() {
    return (eventData, transform, x, y) => {
      const currentControl = this.controls[this.__corner];
      const mouseLocalPosition = this.toLocalPoint(new fabric.Point(x, y), "center", "center");
      const polygonBaseSize = this.getObjectSizeWithStroke();
      const size = this._getTransformedDimensions(0, 0);
      const finalPointPosition = {
        x: (mouseLocalPosition.x * polygonBaseSize.x) / size.x + this.pathOffset.x,
        y: (mouseLocalPosition.y * polygonBaseSize.y) / size.y + this.pathOffset.y
      };
      const newPoints = this.getPoints();
      newPoints[currentControl.pointIndex] = finalPointPosition;
      this.set("points", newPoints);
      this.setCoords();
      return true;
    };
  },

  // define a function that can keep the polygon in the same position when we change its
  // width/height/top/left.
  getControlAnchorWrapper(anchorIndex, fn) {
    return (eventData, transform, x, y) => {
      const absolutePoint = fabric.util.transformPoint(
        {
          x: this.getPoints()[anchorIndex].x - this.pathOffset.x,
          y: this.getPoints()[anchorIndex].y - this.pathOffset.y
        },
        this.calcTransformMatrix()
      );
      const actionPerformed = fn(eventData, transform, x, y);
      this._setPositionDimensions({});
      const polygonBaseSize = this.getObjectSizeWithStroke();
      const newX = (this.points[anchorIndex].x - this.pathOffset.x) / polygonBaseSize.x;
      const newY = (this.points[anchorIndex].y - this.pathOffset.y) / polygonBaseSize.y;
      this.setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
      return actionPerformed;
    };
  },
  getControlPositionHandler(pointIndex) {
    return () => {
      const x = this.getPoints()[pointIndex].x - this.pathOffset.x;
      const y = this.getPoints()[pointIndex].y - this.pathOffset.y;
      return fabric.util.transformPoint(
        { x, y },
        fabric.util.multiplyTransformMatrices(this.canvas.viewportTransform, this.calcTransformMatrix())
      );
    };
  },
  setControls() {
    const lastControl = this.points.length - 1;
    this.controls = this.points.reduce((acc, point, index) => {
      acc[`p${index}`] = new fabric.Control({
        positionHandler: this.getControlPositionHandler(index),
        actionHandler: this.getControlAnchorWrapper(
          index > 0 ? index - 1 : lastControl,
          this.getControlActionHandler()
        ),
        actionName: "modifyPolygon",
        pointIndex: index
      });
      return acc;
    }, {});
  },
  set(key, value) {
    if (key === "fill") this.set("stroke", value);

    this.callSuper("set", key, value);
  }
});
CustomPolygon.fromObject = (object, callback) => {
  const options = {
    ...object,
    fill: object.fill,
    stroke: object.fill || object.stroke
  };

  fabric.Object._fromObject("CustomPolygon", options, callback, "points");
};

fabric.Arrow = Arrow;
fabric.InteractiveText = InteractiveText;
fabric.Rect = CustomRect;
fabric.Ellipse = CustomEllipse;
fabric.Line = CustomLine;
fabric.CustomPolygon = CustomPolygon;
